import React, { useCallback, useMemo } from 'react';
import * as projectsEditDialogConfig from '@material-bank/projects-edit-dialog-config';
import {
  useCurrentProject,
  useProjectsProvider,
  useValidateProjectData,
} from '@material-bank/projects-utils';
import { CustomEventName, shouldTriggerProjectSelectionDialog } from '@mb/lib';
import { MultiselectNavbar } from 'components/components/MultiselectNavbar';
import { useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { sendAnalytics } from '../../api/events';
import { config } from '../../config';
import {
  getEventsMaterialProps,
  pushCompareAnalyticsEvent,
} from '../../helpers';
import useAddToBag from '../../hooks/useAddToBag';
import { definedTranslation } from '../../i18n';
import {
  selectedMaterialsSelector,
  multiSelectToggleSelector,
  appFiltersSelector,
} from '../../redux/selectors/filtersSelector';
import { searchResultsSelector } from '../../redux/selectors/searchSelector';
import {
  setIsCompareTableShown,
  setIsComparisonActive,
  setIsExportModalOpen,
  updateComparisonItems,
} from '../../redux/sliceCreators/commonSlice';
import {
  setSelectedMaterials,
  setMultiSelectToggle,
} from '../../redux/sliceCreators/filtersSlice';
import { ProductSearchMaterial } from '../../types';
import {
  triggerBoardsModal,
  getTaxonomyAnalyticsDataFromFilters,
  isBannersPage,
} from '../../utils/helpers';
import { mapProductToAnalyticsEcommerceItem } from '../../utils/helpers/analytics';

const isBannersSearch = isBannersPage();

interface MultiSelectContainerProps {
  isWithCompareFeature?: boolean;
}

const MultiSelectContainer: React.FC<MultiSelectContainerProps> = ({
  isWithCompareFeature,
}) => {
  const results = useSelector(searchResultsSelector);
  const appFilters = useSelector(appFiltersSelector);
  const selectedMaterials = useSelector(selectedMaterialsSelector);
  const isMultiselect = useSelector(multiSelectToggleSelector);
  const dispatch = useDispatch();
  const { addToBag, isLoading: isBagLoading } = useAddToBag();
  const { currentProject, setCurrentProjectId } = useCurrentProject();
  const { isProjectDataComplete } = useValidateProjectData();
  const { activeProjects } = useProjectsProvider();

  const intl = useIntl();
  const isMobile = useMediaQuery({ query: '(max-width: 767px)' });
  const analyticsEventLocation = isBannersSearch ? 'Collection' : 'Catalog';

  const {
    selectAllTooltip,
    addToBagTooltip,
    compareDisabledText,
    ...textLabels
  } = {
    addToCartButtonLabel: intl?.formatMessage(
      definedTranslation.selectMenu.addToCart,
    ),
    saveButtonLabel: intl?.formatMessage(definedTranslation.selectMenu.save),
    selectAllButtonLabel: intl?.formatMessage(
      definedTranslation.selectMenu.selectAll,
    ),
    cancelButtonLabel: intl?.formatMessage(
      definedTranslation.selectMenu.cancel,
    ),
    shareExportButtonTooltip: intl?.formatMessage(
      definedTranslation.selectMenu.shareExport,
    ),
    itemsSelectedLabel: intl?.formatMessage(
      definedTranslation.selectMenu.itemsSelected,
    ),
    onlySamplesLabel: intl?.formatMessage(
      definedTranslation.selectMenu.onlySamples,
    ),
    selectAllTooltip: intl?.formatMessage(
      definedTranslation.selectMenu.selectAllTooltip,
    ),
    addToBagTooltip: intl?.formatMessage(
      definedTranslation.selectMenu.addToBagTooltip,
    ),
    compareButtonLabel: intl?.formatMessage(
      definedTranslation.compare.toggleLabel,
    ),
    compareDisabledText: intl?.formatMessage(
      definedTranslation.compare.compareLimitReachedlabel,
    ),
  };

  const { id: itemListId, title: itemListName } = useMemo(() => {
    return getTaxonomyAnalyticsDataFromFilters(appFilters);
  }, [appFilters]);

  const handleMultiSelectToggle = () => {
    pushCompareAnalyticsEvent({
      eventName: 'comparison_tool_cancel_cta_clicks',
      projectData: {
        name: String(currentProject?.title),
        id: String(currentProject?.externalId),
      },
      additionalProps: {
        event_items_selected: selectedMaterials?.length || 0,
        interaction_location: 'multiselect_toolbar',
      },
    });
    dispatch(setSelectedMaterials([]));
    dispatch(setMultiSelectToggle(!isMultiselect));
  };

  const handleAssociatedFinishProductsModal = useCallback(() => {
    const itemsAssociatedFinishProducts: any[] = [];

    selectedMaterials.forEach((item: ProductSearchMaterial) => {
      if ((item?.associatedFinishProducts || []).length > 0) {
        const finishItems = {
          associatedProducts: item.associatedFinishProducts,
          parentProduct: {
            id: item.id,
            manufacturerName: item.brand,
            name: item.title || item.name,
            productImageUrl: item.imageUrl,
          },
        };
        itemsAssociatedFinishProducts.push(finishItems);
      }
    });

    if (itemsAssociatedFinishProducts.length > 0) {
      window.dispatchEvent(
        new CustomEvent('open-associated-finishes-products-modal', {
          detail: [...itemsAssociatedFinishProducts],
        }),
      );
    }
  }, [results]);

  const handleCompareClick = () => {
    const compareList = (selectedMaterials || []).slice(0, 5);
    dispatch(updateComparisonItems(compareList));
    dispatch(setSelectedMaterials([]));
    dispatch(setMultiSelectToggle(!isMultiselect));
    dispatch(setIsComparisonActive(true));
    if (compareList?.length > 1) {
      pushCompareAnalyticsEvent({
        eventName: 'comparison_tool_compare_cta_clicks',
        projectData: {
          name: String(currentProject?.title),
          id: String(currentProject?.externalId),
        },
        additionalProps: {
          event_items_selected: compareList?.length || 0,
        },
      });
      dispatch(setIsCompareTableShown(true));
    }
  };

  const onProjectSelect = (projectId: number) => {
    setCurrentProjectId(projectId);
  };

  const handleAddToBag = () => {
    const selectedProject = {
      id: currentProject?.externalId,
      label: currentProject?.title ?? 'Select project..',
    };

    if (Object.keys(activeProjects).length === 0) {
      window.dispatchEvent(
        new CustomEvent(CustomEventName.OPEN_CREATE_PROJECT_MODAL, {
          detail: {
            analyticsEventLocation,
            successCallback: (project: any) => {
              onProjectSelect?.(project?.externalId || project?.project_id);

              addToBag({
                materials: selectedMaterials,
                projectId:
                  String(project?.project_id) || String(project?.externalId),
                project,
              });
            },
          },
        }),
      );

      return;
    }

    if (shouldTriggerProjectSelectionDialog()) {
      window.dispatchEvent(
        new CustomEvent(CustomEventName.OPEN_PROJECT_SELECTION_DIALOG, {
          detail: {
            currentProjectId: selectedProject?.id,
            analyticsEventLocation,
            onCurrentProjectSave: (project: any) => {
              onProjectSelect?.(project?.externalId || project?.project_id);

              // No validation needed here - the project has been validated in the selection dialog
              addToBag({
                materials: selectedMaterials,
                projectId:
                  String(project?.project_id) || String(project?.externalId),
                project,
              });
            },
          },
        }),
      );
      return;
    }

    // If the project is not found, open the create project modal
    if (!currentProject) {
      window.dispatchEvent(
        new CustomEvent('open-create-project-modal', {
          detail: {
            eventLocation: 'Search',
          },
        }),
      );

      return;
    }

    const addToBagPros = {
      materials: selectedMaterials,
      projectId: String(currentProject?.externalId),
    };

    // Validate the project data
    if (currentProject && !isProjectDataComplete(currentProject)) {
      projectsEditDialogConfig.openProjectEditDialog({
        projectId: currentProject.externalId,
        successCallback: () => {
          addToBag(addToBagPros);
        },
      });

      return;
    }

    addToBag(addToBagPros);

    handleAssociatedFinishProductsModal();
  };

  const handleAddToBoard = async () => {
    const ids =
      selectedMaterials?.map((item: ProductSearchMaterial) => item.entityId) ??
      [];
    const analyticsProps = getEventsMaterialProps(selectedMaterials?.[0]);
    const addToWishlistEventItems =
      selectedMaterials?.map((selectedMaterial: ProductSearchMaterial) =>
        mapProductToAnalyticsEcommerceItem({
          product: selectedMaterial,
          itemListId,
          itemListName,
          project: currentProject,
        }),
      ) ?? [];

    const currencies = {
      US: 'USD',
      EU: 'EUR',
      JP: 'JPY',
    };

    const itemsData = selectedMaterials.map((item: any) => {
      const type: 'finish' | 'material' = item.productFinishType
        ?.toLowerCase()
        ?.includes('finish material')
        ? 'finish'
        : 'material';

      return {
        id: item.entityId,
        type,
      };
    });

    if (ids.length > 0) {
      triggerBoardsModal({
        itemsData,
        onSuccessAnalyticsEvents: [
          { ecommerce: null },
          {
            event: 'add_to_wishlist',
            list_id: itemListId,
            list_name: itemListName,
            method:
              results.length === selectedMaterials.length
                ? 'Add all'
                : 'Multi-select',
            type: 'Materials',
            name: null,
            action: null,
            experiment_name: null,
            experiment_version: null,
            event_location: isBannersSearch ? 'Collection' : 'Catalog',
            ecommerce: {
              currency: currencies[ENVIRONMENT_REGION],
              value: 0,
              items: addToWishlistEventItems,
            },
          },
        ],
      });
      const stringIds = ids.map((id: any) => String(id));
      const value = {};
      await sendAnalytics({
        id: stringIds,
        type: 'favorite',
        ...analyticsProps,
        ...value,
      });
    }
  };

  const handleExportClick = () => {
    dispatch(setIsExportModalOpen(true));
  };

  const handleSelectAll = () => {
    dispatch(
      setSelectedMaterials(
        results.map((product: any) => {
          if (product?.selectedColorFilterEntityId) {
            return {
              ...product,
              entityId: product.selectedColorFilterEntityId,
            };
          }

          return product;
        }),
      ),
    );
  };

  const isSelectAllDisabled = results?.length >= config.maxSelectNumber;
  const isProductInSelected = selectedMaterials.find(
    (item: ProductSearchMaterial) => item?.isProduct,
  );
  const isDigitalInSelected = selectedMaterials.find(
    (item: ProductSearchMaterial) => item?.isDigital,
  );
  const isAddToBagDisabled =
    isBagLoading ||
    isProductInSelected ||
    isDigitalInSelected ||
    selectedMaterials?.length === 0;

  return (
    <MultiselectNavbar
      isWithCompareFeature={isWithCompareFeature && !isMobile}
      isCompareButtonDisabled={selectedMaterials?.length > 5}
      compareDisabledText={
        selectedMaterials?.length > 5 ? compareDisabledText : undefined
      }
      shouldShowExportImport={!isDigitalInSelected}
      isAddToBagDisabled={!!isAddToBagDisabled}
      shouldShowSamplesSwitch={false}
      shouldShowDeleteButton={false}
      isSamplesSwitchEnabled={false}
      selectedNumber={selectedMaterials?.length ?? 0}
      onCancel={handleMultiSelectToggle}
      onAddToBoard={handleAddToBoard}
      onAddToBag={handleAddToBag}
      onExportClick={handleExportClick}
      onSelectAll={handleSelectAll}
      onCompareClick={handleCompareClick}
      isSelectAllDisabled={isSelectAllDisabled}
      selectAllTooltip={isSelectAllDisabled ? selectAllTooltip : undefined}
      addToBagTooltip={isProductInSelected ? addToBagTooltip : undefined}
      {...textLabels}
    />
  );
};

export default MultiSelectContainer;
